
import { useEffect, useMemo, useState } from 'react';
import { Combobox, Input, Select, type ComboboxOption } from '@/shared/ui';
import { cachedFetch } from '@/shared/lib/refCache';
import { directoriesApi, type DirectoryOption } from '@/shared/api';
import { directoryValues, type DirectoryValue, type FormField } from '@/shared/api/forms';
import styles from './DirectoryField.module.css';

const SEARCH_FROM = 20;
const CACHE_TTL_MS = 60_000;

export interface DirectoryFieldProps {
  field: FormField;
  value: unknown;
  id?: string;
  'data-in-field'?: string;
  parentValue?: unknown;
  disabled?: boolean;
  onChange: (value: unknown) => void;
}

export const DirectoryField: React.FC<DirectoryFieldProps> = ({
  field,
  value,
  id,
  parentValue,
  disabled,
  onChange,
  ...rest
}) => {
  const fieldProps = { id, 'data-in-field': rest['data-in-field'] };
  const directoryId = field.source?.directoryId ?? null;
  const [options, setOptions] = useState<DirectoryOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);
  const [customText, setCustomText] = useState('');

  useEffect(() => {
    if (!directoryId) return;
    setLoading(true);
    setFailed(false);
    cachedFetch(`directory:${directoryId}`, CACHE_TTL_MS, () =>
      directoriesApi.options(directoryId).then((res) => res.data),
    )
      .then((list) => setOptions(list))
      .catch(() => setFailed(true))
      .finally(() => setLoading(false));
  }, [directoryId]);

  const selected = useMemo(() => directoryValues(value), [value]);
  const multiple = field.display?.multiple === true;
  const parentId = directoryValues(parentValue)[0]?.id ?? null;
  const parentRequired = Boolean(field.source?.parentFromField);

  const available = useMemo(() => {
    if (!parentRequired) return options;
    if (!parentId) return [];
    return options.filter((option) => option.parentEntryId === parentId);
  }, [options, parentRequired, parentId]);

  const groupBy = field.display?.groupBy;
  const comboOptions: ComboboxOption[] = useMemo(() => {
    const fromDirectory = available.map((option) => ({
      value: option.id,
      label: option.label,
      subtitle: option.subtitle,
      keywords: option.code,
      group: groupBy && groupBy !== 'none' ? option.subtitle : null,
    }));
    const custom = selected
      .filter((item) => item.custom)
      .map((item) => ({ value: `custom:${item.label}`, label: item.label }));
    return [...fromDirectory, ...custom];
  }, [available, selected, groupBy]);

  const searchable =
    field.display?.search === 'always' ||
    (field.display?.search !== 'never' && available.length >= SEARCH_FROM);
  const asCombobox = searchable || multiple || field.allowOther === true;

  const toKey = (item: DirectoryValue) => (item.custom ? `custom:${item.label}` : (item.id ?? ''));

  const toSnapshot = (key: string): DirectoryValue | null => {
    if (key.startsWith('custom:')) return { label: key.slice('custom:'.length), custom: true };
    const option = available.find((o) => o.id === key);
    return option ? { id: option.id, label: option.label } : null;
  };

  const addCustom = () => {
    const label = customText.trim();
    if (label === '') return;
    const snapshot: DirectoryValue = { label, custom: true };
    onChange(multiple ? [...selected, snapshot] : snapshot);
    setCustomText('');
  };

  if (!directoryId) {
    return <p className={styles.notice}>Справочник для этого поля не выбран.</p>;
  }
  if (failed) {
    return <p className={styles.notice}>Не удалось загрузить справочник. Обновите страницу.</p>;
  }

  const blockedByParent = parentRequired && !parentId;
  const placeholder = blockedByParent
    ? 'Сначала выберите значение выше'
    : loading
      ? 'Загрузка…'
      : available.length === 0
        ? 'Список пуст'
        : (field.placeholder ?? 'Выберите…');

  const emptyText = available.length === 0 ? 'В справочнике нет записей' : 'Ничего не найдено';

  const footer = field.allowOther ? (
    <>
      <Input
        value={customText}
        onChange={(e) => setCustomText(e.target.value)}
        placeholder="Нет в списке — впишите"
        aria-label="Свой вариант"
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            addCustom();
          }
        }}
      />
      <button type="button" className={styles.customAdd} onClick={addCustom}>
        Добавить
      </button>
    </>
  ) : undefined;

  if (!asCombobox) {
    return (
      <Select
        value={selected[0] ? toKey(selected[0]) : ''}
        onValueChange={(key) => onChange(key ? (toSnapshot(key) ?? undefined) : undefined)}
        options={comboOptions.map((option) => ({ value: option.value, label: option.label }))}
        placeholder={placeholder}
        disabled={disabled || blockedByParent}
        aria-label={field.label}
        {...fieldProps}
      />
    );
  }

  if (multiple) {
    return (
      <Combobox
        multiple
        value={selected.map(toKey).filter(Boolean)}
        onValueChange={(keys) =>
          onChange(keys.map(toSnapshot).filter((item): item is DirectoryValue => item != null))
        }
        options={comboOptions}
        placeholder={placeholder}
        disabled={disabled || blockedByParent}
        emptyText={emptyText}
        aria-label={field.label}
        footer={footer}
        {...fieldProps}
      />
    );
  }

  return (
    <Combobox
      value={selected[0] ? toKey(selected[0]) : null}
      onValueChange={(key) => onChange(key ? (toSnapshot(key) ?? undefined) : undefined)}
      options={comboOptions}
      placeholder={placeholder}
      disabled={disabled || blockedByParent}
      emptyText={emptyText}
      aria-label={field.label}
      footer={footer}
      {...fieldProps}
    />
  );
};
